<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import SaveToFolderModal from '@/components/SaveToFolderModal.vue'
import TextLinkButton from '@/components/TextLinkButton.vue'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { betaOptIn } from '@/util/beta_opt_in'
import { LIBRARY_EVENT_NAMES } from './library_event_names'
import type { CopyGoalProps } from './library_types'

const props = withDefaults(defineProps<CopyGoalProps>(), {
  gradeLevelGoal: '',
  objectives: () => [],
  sourceFolderUuid: ''
})

const router = useRouter()
const { userId } = storeToRefs(useUserStore())
const { profileId } = storeToRefs(useContentProfileStore())
const analytics = getAnalyticsTracker()

const isModalOpen = ref(false)

const copyPayload = computed(() => ({
  goal_uuid: props.sourceGoalUuid,
  user_uuid: userId.value,
  content_profile_id: profileId.value,
  grade_level_goal: props.gradeLevelGoal,
  save_by_copy: true,
  personalized: props.isPersonalized,
  objectives: props.objectives
}))

function openCopiedGoal(folder: { uuid: string }, savedContent: unknown) {
  const copiedGoalUuid = (savedContent as { uuid?: unknown } | null | undefined)?.uuid
  if (typeof copiedGoalUuid !== 'string' || !copiedGoalUuid) {
    console.error('error copying goal to another folder', savedContent)
    return
  }

  analytics.sendEvent(LIBRARY_EVENT_NAMES.COPIED_CONTENT_FOR_FOLDER, {
    content_type: CONTENT_TYPES.SAVED_SUPER_PAGE,
    copied_for_uuid: folder.uuid
  })
  router.push({
    name: 'SavedAnchorPage',
    params: { uuid: copiedGoalUuid },
    query: betaOptIn(router.currentRoute.value.query)
  })
}
</script>

<template>
  <TextLinkButton text="Copy goal to another folder" icon-name="content_copy" @click="isModalOpen = true" />
  <SaveToFolderModal
    v-if="isModalOpen"
    :content-type="CONTENT_TYPES.SAVED_SUPER_PAGE"
    content-type-label="Goal"
    :content-uuid="sourceGoalUuid"
    :content-payload="copyPayload"
    custom-header="Copy Goal To Another Folder"
    :excluded-folder-uuid="sourceFolderUuid"
    @update:visible="isModalOpen = $event"
    @save="openCopiedGoal"
  />
</template>
